export type ThemeMode = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'themeMode';

/**
 * Gets the current system theme preference
 * @returns 'dark' if system prefers dark mode, 'light' otherwise
 */
export const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Applies the theme to the body element
 * @param theme - The theme to apply ('light', 'dark', or 'system')
 */
export const applyTheme = (theme: ThemeMode): void => {
  const isDark = theme === 'dark' || (theme === 'system' && getSystemTheme() === 'dark');

  if (isDark) {
    document.body.classList.add('dark');
  } else {
    document.body.classList.remove('dark');
  }
};

/**
 * Gets the saved theme from localStorage
 * @returns The saved theme mode, or 'system' as default
 */
export const getSavedTheme = (): ThemeMode => {
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === 'light' || saved === 'dark' || saved === 'system') {
    return saved;
  }

  // Migrate old boolean darkMode to new string-based theme
  const oldDarkMode = localStorage.getItem('darkMode');
  if (oldDarkMode === 'true') {
    return 'dark';
  }

  return 'system';
};

/**
 * Saves the theme to localStorage
 * @param theme - The theme to save
 */
export const saveTheme = (theme: ThemeMode): void => {
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  // Remove old darkMode key if it exists
  localStorage.removeItem('darkMode');
};

/**
 * Applies theme from storage and returns the theme mode
 * @returns The theme mode that was applied
 */
export const applyDarkModeFromStorage = (): boolean => {
  const theme = getSavedTheme();
  applyTheme(theme);
  // Return boolean for backwards compatibility
  return theme === 'dark' || (theme === 'system' && getSystemTheme() === 'dark');
};

/**
 * Sets up a listener for system theme changes
 * @param callback - Function to call when system theme changes
 * @returns Cleanup function to remove the listener
 */
export const listenToSystemTheme = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {
      // No-op for SSR environments
    };
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  mediaQuery.addEventListener('change', callback);
  return () => mediaQuery.removeEventListener('change', callback);
};

/**
 * Removes dark mode class from the body element
 */
export const removeDarkMode = (): void => {
  document.body.classList.remove('dark');
};
